"use client";

import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MDEditor from '@uiw/react-md-editor';
import { getCollectionNotice, saveCollectionNotice, CollectionNotice } from '@/services/policyService';
import { getCompanyInfo } from '@/services/companyService';
import { getUserInfo } from '@/services/userService';
import { useTheme } from '@/contexts/ThemeContext';
import { getAuthUID } from '@/lib/authState';
import { toast } from 'sonner';
import { Loader2, Upload, FileText, PenTool, Eye, FileUp } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { useOnboarding } from "@/onboarding/OnboardingContext";

// Collection Notice template
const COLLECTION_NOTICE_TEMPLATE = `# Privacy Collection Notice

| | |
|---|---|
| **Who we are?** | Your personal information is being collected by [insert full entity details and ACN] (we, our, us, **[name of entity]**)<br><br>Contact details:<br>[Team name] Phone: [number] Email: [address] |
| **What is in this notice?** | This notice describes how we collect, hold, use, and share your Personal Information.<br><br>It includes:<br>• the kinds of Personal Information we collect;<br>• the reasons why we collect and use that information;<br>• who we share your Personal Information with;<br>• consequences if we are unable to collect your Personal Information; and<br>• how to access our Privacy Policy or contact us about our privacy practices. |
| **What is Personal Information?** | Personal Information includes information or an opinion about an individual that identifies or can be used to reasonably identify them. This can include a person's name, age, gender, employment history, health information, residential address, and contact details. |
| **What information we collect?** | We collect Personal Information about you when you engage with us directly or via a platform we operate. Personal Information we collect from you includes but not limited to:<br>• information about your identity, such as your name and contact details (including your email and mobile number);<br>• your employment history;<br>• your skills and qualifications;<br>• information about your physical health; and<br>• Information contained in identity documents (for example your driver's licence or passport).<br><br>We may collect this information from you, a person acting on your behalf or a third-party service provider (such as a recruiter) who has captured your information and shared it with us.<br><br>If you give us information about others, you must have authority to do so and tell them what is in this notice. |
| **Why is your Personal Information being collected?** | The purpose of collecting your personal information is:<br>• to confirm your identity and manage our relationship with you, including contacting you about any changes to products and services we have provided you;<br>• to identify, manage and minimise security risks and prevent fraud, scams, and other authorised activities;<br>• [to assess your qualifications and experience against any job opening we have];<br>• for any other purpose you consent to (or where you "opt-in"); and<br>• for any other purpose set out in our Privacy Policy.<br><br>[Optional - include if there are any legal or regulatory requirements for collection: We are required to collect and hold this information to comply with our obligations under [insert applicable legal or regulatory requirements].] |
| **What would happen if your Personal Information was not collected?** | If you don't provide or if we are unable to collect your information, or the information you give us is inaccurate or incomplete, we may not be able to [insert consequences if personal information is not collected, for example we are unable to provide you with our product or service]. |
| **Who we share your Personal Information with?** | The information we hold about you will be shared with our associated business entities. It may also be shared with: [insert details of who else you think you may share Personal Information with].<br><br>Sometimes we may send your information overseas, including to:<br>• overseas businesses that we are associated with;<br>• service providers or third parties who store data or operate outside Australia;<br>• complete a transaction such as an international money transfer; or<br>• comply with laws, assist government, and law enforcement agencies.<br><br>Your information may be disclosed to recipients in the following countries: [insert countries] |
| **Access to and correction of Personal Information and complaints** | Please refer to our Privacy Policy for information on how to get access to or correct the Personal Information we hold about you or how to make a privacy related complaint. |
`;

export default function ModalCollectionNoticeImportExample() {
  const [open, setOpen] = useState(true);
  const { closeActive, setStatus, openStep, goBack } = useOnboarding();
  const [policy, setPolicy] = useState<CollectionNotice | null>(null);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('editor');
  const [abnAcn, setAbnAcn] = useState<string>('');
  const [userInfo, setUserInfo] = useState<{ email?: string; firstName?: string; lastName?: string; phoneNumber?: string } | null>(null);

  useEffect(() => {
    if (open) {
        fetchPolicy();
        fetchCompanyInfo();
        fetchUserInfo();
    }
  }, [open]);

  const fetchCompanyInfo = async () => {
    try {
      const info = await getCompanyInfo();
      if (info?.abnAcn) {
        setAbnAcn(info.abnAcn);
      }
    } catch (error) {
      console.error("Failed to fetch company info", error);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const info = await getUserInfo();
      if (info) {
        setUserInfo(info);
      }
    } catch (error) {
      console.error("Failed to fetch user info", error);
    }
  };

  const fetchPolicy = async () => {
    setLoading(true);
    try {
      const companyID = window.companyID;
      if (!companyID) {
          console.warn("No companyID found");
          return;
      }
      const data = await getCollectionNotice(companyID);
      setPolicy(data);
      const content = data?.content ? decodeURIComponent(data.content) : '';
      setDraft(content);
    } catch (error) {
      console.error("Failed to fetch collection notice", error);
      toast.error("Failed to fetch collection notice");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const companyID = window.companyID;
      const uid = getAuthUID();
      if (!companyID || !uid) throw new Error("Missing company ID or User ID");

      await saveCollectionNotice(
        companyID,
        draft,
        uid,
        policy?.version || '0.0',
        policy?.content ? decodeURIComponent(policy.content) : ''
      );

      toast.success("Collection Notice saved successfully");
      setStatus("import_collection_notice", "done");
      openStep("control_selection");
      await fetchPolicy();
    } catch (error) {
      console.error("Failed to save collection notice", error);
      toast.error("Failed to save collection notice");
    } finally {
      setSaving(false);
    }
  };

  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    setUploading(true);

    try {
      // Handle DOCX files
      if (fileName.endsWith('.docx')) {
        const arrayBuffer = await file.arrayBuffer();
        const mammoth = await import('mammoth');
        
        // Convert to HTML first, then to simple markdown-like text
        const result = await mammoth.convertToHtml({ arrayBuffer });
        
        // Convert HTML to markdown-ish format
        let markdown = result.value
          .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n')
          .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n')
          .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n')
          .replace(/<h4[^>]*>(.*?)<\/h4>/gi, '#### $1\n\n')
          .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')
          .replace(/<b>(.*?)<\/b>/gi, '**$1**')
          .replace(/<em>(.*?)<\/em>/gi, '*$1*')
          .replace(/<i>(.*?)<\/i>/gi, '*$1*')
          .replace(/<li>(.*?)<\/li>/gi, '- $1\n')
          .replace(/<\/ul>/gi, '\n')
          .replace(/<\/ol>/gi, '\n')
          .replace(/<ul[^>]*>/gi, '')
          .replace(/<ol[^>]*>/gi, '')
          .replace(/<p>(.*?)<\/p>/gi, '$1\n\n')
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<[^>]+>/g, '') // Remove remaining HTML tags
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/\n{3,}/g, '\n\n') // Normalize multiple newlines
          .trim();
        
        setDraft(markdown);
        setActiveTab('editor');
        toast.success('Word document converted and loaded');
      }
      // Handle PDF files
      else if (fileName.endsWith('.pdf')) {
        const arrayBuffer = await file.arrayBuffer();
        const pdfjs = await import('pdfjs-dist');
        
        // Configure worker
        pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
        
        const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
        let fullText = '';
        
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const pageText = textContent.items
            .map((item: any) => item.str)
            .join(' ');
          fullText += pageText + '\n\n';
        }
        
        setDraft(fullText.trim());
        setActiveTab('editor');
        toast.success('PDF text extracted and loaded');
      }
      // Handle text/markdown files
      else if (fileName.endsWith('.txt') || fileName.endsWith('.md')) {
        const text = await file.text();
        setDraft(text);
        setActiveTab('editor');
        toast.success('File content loaded into editor');
      }
      else {
        toast.error('Unsupported file type. Please use .docx, .pdf, .txt, or .md files.');
      }
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to process file. Please try again or copy/paste the content manually.');
    } finally {
      setUploading(false);
      // Reset the input so the same file can be selected again
      e.target.value = '';
    }
  };

  /**
   * Replace template placeholders with actual company data
   */
  const fillTemplateVariables = (template: string): string => {
    const companyName = window.companyName || '[Company Name]';
    // Use fetched user info for contact details
    const ownerEmail = userInfo?.email || '[email address]';
    const ownerName = userInfo?.firstName && userInfo?.lastName 
      ? `${userInfo.firstName} ${userInfo.lastName}` 
      : userInfo?.firstName || '[Team name]';
    const ownerPhone = userInfo?.phoneNumber || '[number]';
    
    // Build full entity details string with ACN if available
    const fullEntityDetails = abnAcn 
      ? `${companyName} (${abnAcn.includes('ACN') || abnAcn.includes('ABN') ? abnAcn : `ACN ${abnAcn}`})`
      : `${companyName} (ACN xxx)`;
    
    return template
      // Company/entity name variations
      .replace(/\[insert full entity details and ACN\]/g, fullEntityDetails)
      .replace(/\[name of entity\]/g, companyName)
      .replace(/\*\*\[name of entity\]\*\*/g, `**${companyName}**`)
      // Contact details
      .replace(/\[Team name\]/g, ownerName)
      .replace(/\[address\]/g, ownerEmail)
      .replace(/\[number\]/g, ownerPhone);
  };

  const applyTemplate = (templateContent: string) => {
      const filledTemplate = fillTemplateVariables(templateContent);
      setDraft(filledTemplate);
      setActiveTab('editor');
      toast.success("Template applied with your company details");
  };

  const templates = [
      { name: "Standard Collection Notice", content: COLLECTION_NOTICE_TEMPLATE },
  ];

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open Collection Notice Editor</Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) closeActive(); }}>
        <DialogContent className="w-full! max-w-[95vw]! xl:max-w-[85vw]! 2xl:max-w-[1600px]! h-[95vh]! md:h-[90vh]! bg-transparent shadow-none border-0 p-0 flex items-center justify-center">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full h-full overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Close button */}
            <button
              onClick={() => { setOpen(false); closeActive(); }}
              className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
            </button>
            
            {/* Header with tabs and actions */}
            <div className="shrink-0 p-4 md:p-6 pb-0 border-b border-white/10 bg-linear-to-b from-primary/5 to-transparent">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">Add your Collection Notice</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Select from one of the options below to add your Collection Notice to de.iterate</p>
                </div>
                <div className="flex gap-2 shrink-0 mr-10">
                  <Button variant="outline" size="sm" onClick={() => { setOpen(false); goBack(); }}>Back</Button>
                  <Button size="sm" onClick={handleSave} disabled={saving}>
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save & Continue
                  </Button>
                </div>
              </div>
              
              {!loading && (
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="bg-transparent p-0 gap-1 md:gap-4 flex-wrap">
                    <TabsTrigger value="editor" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary border border-transparent data-[state=active]:border-primary/20 rounded-md px-3 py-1.5 text-sm">
                      <PenTool className="w-4 h-4 mr-1.5" /> Editor
                    </TabsTrigger>
                    <TabsTrigger value="upload" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary border border-transparent data-[state=active]:border-primary/20 rounded-md px-3 py-1.5 text-sm">
                      <Upload className="w-4 h-4 mr-1.5" /> Upload
                    </TabsTrigger>
                    <TabsTrigger value="templates" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary border border-transparent data-[state=active]:border-primary/20 rounded-md px-3 py-1.5 text-sm">
                      <FileText className="w-4 h-4 mr-1.5" /> Templates
                    </TabsTrigger>
                    <TabsTrigger value="preview" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary border border-transparent data-[state=active]:border-primary/20 rounded-md px-3 py-1.5 text-sm">
                      <Eye className="w-4 h-4 mr-1.5" /> Preview
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-hidden w-full">
                {loading ? (
                    <div className="h-full flex justify-center items-center">
                        <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                ) : (
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full w-full flex flex-col">
                        <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-black/5 w-full">
                            <TabsContent value="editor" className="h-full mt-0 m-0 w-full">
                                <MDEditor
                                    value={draft}
                                    onChange={v => setDraft(v || '')}
                                    data-color-mode={isDark ? 'dark' : 'light'}
                                    className="h-full w-full shadow-none border border-white/10 rounded-md overflow-hidden"
                                    preview="edit"
                                    hideToolbar={false}
                                    style={{ minHeight: 'calc(100vh - 280px)', width: '100%' }}
                                />
                            </TabsContent>

                            <TabsContent value="upload" className="h-full mt-0 m-0 flex flex-col items-center justify-center">
                                <div className="border-2 border-dashed border-white/20 rounded-xl p-8 md:p-12 text-center bg-white/5 backdrop-blur-sm max-w-2xl w-full">
                                    {uploading ? (
                                      <>
                                        <Loader2 className="w-12 h-12 mx-auto text-primary mb-4 animate-spin" />
                                        <h3 className="text-lg font-medium mb-2">Processing Document...</h3>
                                        <p className="text-sm text-muted-foreground">Converting your document to editable format</p>
                                      </>
                                    ) : (
                                      <>
                                        <FileUp className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                        <h3 className="text-lg font-medium mb-2">Upload Document</h3>
                                        <p className="text-sm text-muted-foreground mb-2">Supports Word (.docx), PDF, Markdown, and Text files.</p>
                                        <p className="text-xs text-muted-foreground/70 mb-6">Your document will be converted to editable text format.</p>
                                        <input
                                            id="cn-upload"
                                            type="file"
                                            className="hidden"
                                            accept=".docx,.pdf,.md,.txt"
                                            onChange={handleFileUpload}
                                        />
                                        <Button asChild variant="outline">
                                            <label htmlFor="cn-upload" className="cursor-pointer">
                                                Select File
                                            </label>
                                        </Button>
                                      </>
                                    )}
                                </div>
                            </TabsContent>

                            <TabsContent value="templates" className="h-full mt-0 m-0">
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                    {templates.map((t, i) => (
                                        <div key={i} className="p-4 border border-white/10 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer" onClick={() => applyTemplate(t.content)}>
                                            <h3 className="font-medium mb-2">{t.name}</h3>
                                            <p className="text-xs text-muted-foreground">Use our generic template to get you started, making sure you have your legal team review and approve this policy is correct and inline with your businesses obligations under the Privacy Act.</p>
                                            <Button size="sm" variant="secondary" className="mt-4 w-full">Use Template</Button>
                                        </div>
                                    ))}
                                </div>
                            </TabsContent>

                            <TabsContent value="preview" className="h-full mt-0 m-0 overflow-y-auto">
                                <div className="prose dark:prose-invert max-w-none p-6 md:p-8 bg-card rounded-md border border-white/10">
                                    <ReactMarkdown remarkPlugins={[[remarkGfm, { breaks: true }]]}>
                                        {draft || '*No content to preview.*'}
                                    </ReactMarkdown>
                                </div>
                            </TabsContent>
                        </div>
                    </Tabs>
                )}
            </div>

          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
